/**
 * teamSessionRegistry.ts
 *
 * Simple pub/sub registry for team channel agent sessions.
 * Maps team session keys to their callId and completion callbacks.
 * Allows the agentEventHandler to notify the team channel when a response is complete.
 */

export type TeamToolCallEntry = {
  toolCallId: string;
  name: string;
  status: 'running' | 'done' | 'error';
  input?: string;
  output?: string;
};

type TeamSessionEntry = {
  callId: string;
  agentId: string;
  agentName: string;
  onText: (text: string, isDelta: boolean) => void;
  onDone: (text: string) => void;
  onThinking?: (content: string) => void;
  onToolCall?: (toolCall: TeamToolCallEntry) => void;
};

const registry = new Map<string, TeamSessionEntry>();

export function registerTeamSession(sessionKey: string, entry: TeamSessionEntry): void {
  registry.set(sessionKey, entry);
}

export function unregisterTeamSession(sessionKey: string): void {
  registry.delete(sessionKey);
}

export function isTeamSession(sessionKey: string): boolean {
  return sessionKey.includes(':team-');
}

export function getTeamSession(sessionKey: string): TeamSessionEntry | undefined {
  return registry.get(sessionKey);
}

export function notifyTeamDelta(sessionKey: string, text: string): void {
  const entry = registry.get(sessionKey);
  if (entry) {
    entry.onText(text, true);
  }
}

export function notifyTeamDone(sessionKey: string, fullText: string): void {
  const entry = registry.get(sessionKey);
  if (entry) {
    entry.onDone(fullText);
    registry.delete(sessionKey);
  }
}

export function notifyTeamThinking(sessionKey: string, content: string): void {
  const entry = registry.get(sessionKey);
  if (entry?.onThinking) {
    entry.onThinking(content);
  }
}

export function notifyTeamToolCall(sessionKey: string, toolCall: TeamToolCallEntry): void {
  const entry = registry.get(sessionKey);
  if (entry?.onToolCall) {
    entry.onToolCall(toolCall);
  }
}

export function clearTeamSessions(): void {
  registry.clear();
}
